import React, { useMemo, useState, useEffect, useRef } from 'react';
import './table-control.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { setGlobalFilter } from './GlobalFilterSlice';
import { ReactComponent as ScanIcon } from '../../images/scan.svg';
import { ReactComponent as DatabaseIcon } from '../../images/database-01.svg';
import { ReactComponent as RepeatIcon } from '../../images/repeat-04.svg';
import { ReactComponent as SendUploadIcon } from '../../images/upload-04.svg';
import { ReactComponent as SearchIcon } from '../../images/search-light.svg';
import { ReactComponent as FilterIcon } from '../../images/filter-funnel-02.svg';
import ToggleOption from '../../components/common/ToggleOption';
import { Checkbox } from '../common/CustomCheckbox/CheckBox';
import SetpointGroupFilterSelector from './setpoint-group-filter/SetpointGroupFilterSelector';
import { useAppSelector } from '../../hooks/storeHooks';
import { useToastService } from '../../components/toast/ToastService';
import { RootState } from '../../store';
import { clearSelectedRows } from './parameterGroupsSlice';
import { ProtectAction } from '../../rbac';

interface TableControlProps {
  onShowBackupToggle?: (isActive: boolean) => void;
  toggleGlobalSelectAll: (flag: boolean) => void;
  globalSelectAll: boolean;
  availableGroups: { id: string; label: string; parameterCount: number; groupName: string }[];
  selectedGroupIds: string[];
  onFilterApply: (selectedGroupIds: string[]) => void;
  readController: () => Promise<boolean>;
  handleWriteClick: () => void;
  handleBackupClick: () => void;
  handleRestoreClick: () => void;
  rPermission: { [key: string]: boolean };
}
export default function Main({
  onShowBackupToggle,
  toggleGlobalSelectAll,
  globalSelectAll,
  availableGroups,
  selectedGroupIds,
  onFilterApply,
  readController,
  handleWriteClick,
  handleBackupClick,
  handleRestoreClick,
  rPermission,
}: TableControlProps) {
  const { assetId } = useParams<{ assetId: string }>();
  const [searchValue, setSearchValue] = useState('');
  const [isToggleActive, setIsToggleActive] = useState(false);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const dispatch = useDispatch();
  const selectedRowsArray = useSelector((state: RootState) => state.parameterGroups.selectedRows);
  const selectedRows = useMemo(() => new Set<string>(selectedRowsArray), [selectedRowsArray]);

  const { showErrorToast, showInfoToast } = useToastService();
  // Ref to track timeout id for read operation
  const readTimeoutRef = useRef<number | null>(null);
  // Track if a read operation is currently awaiting websocket fulfillment
  const [isReadPending, setIsReadPending] = useState(false);

  const readItems = useAppSelector((state) => state.parameterGroups.readItems);
  const enableWrite = Object.keys(readItems).length > 0;

  // Clear search when asset changes
  useEffect(() => {
    setSearchValue('');
    dispatch(setGlobalFilter(''));
  }, [assetId, dispatch]);

  const handleReadController = async () => {
    const readStatus = await readController();
    // Makeing sure to clear any existing timeout before starting a new read
    if (readTimeoutRef.current) {
      window.clearTimeout(readTimeoutRef.current);
      readTimeoutRef.current = null;
    }
    if (readStatus) {
      setIsReadPending(true);
      // Start 60s timeout to warn user if websocket hasn't delivered results
      readTimeoutRef.current = window.setTimeout(() => {
        if (isReadPending) {
          showErrorToast('Request Timeout', 'Controller is still processing setpoints. Please wait or retry.');
        }
      }, 60000); // 60 seconds
      showInfoToast({
        headerMessage: 'Read in progress',
        icon: (
          <svg xmlns='http://www.w3.org/2000/svg' width={20} height={20} viewBox='0 0 20 20' fill='none'>
            <path
              d='M6.66667 2.5H6.5C5.09987 2.5 4.3998 2.5 3.86502 2.77248C3.39462 3.01217 3.01217 3.39462 2.77248 3.86502C2.5 4.3998 2.5 5.09987 2.5 6.5V6.66667M6.66667 17.5H6.5C5.09987 17.5 4.3998 17.5 3.86502 17.2275C3.39462 16.9878 3.01217 16.6054 2.77248 16.135C2.5 15.6002 2.5 14.9001 2.5 13.5V13.3333M17.5 6.66667V6.5C17.5 5.09987 17.5 4.3998 17.2275 3.86502C16.9878 3.39462 16.6054 3.01217 16.135 2.77248C15.6002 2.5 14.9001 2.5 13.5 2.5H13.3333M17.5 13.3333V13.5C17.5 14.9001 17.5 15.6002 17.2275 16.135C16.9878 16.6054 16.6054 16.9878 16.135 17.2275C15.6002 17.5 14.9001 17.5 13.5 17.5H13.3333M2.5 10H2.50833M6.25 10H6.25833M13.75 10H13.7583M10 10H10.0083M17.5 10H17.5083'
              stroke='#60BFDA'
              strokeWidth={2}
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        ),
      });
      dispatch(clearSelectedRows());
      toggleGlobalSelectAll(false);
    } else {
      setIsReadPending(false);
      showErrorToast('Controller scan failure.');
    }
  };

  // Cancel pending read when data arrives (readItems populated) or on unmount
  useEffect(() => {
    if (isReadPending && Object.keys(readItems).length > 0) {
      setIsReadPending(false);
      if (readTimeoutRef.current) {
        window.clearTimeout(readTimeoutRef.current);
        readTimeoutRef.current = null;
      }
    }
  }, [isReadPending, readItems]);

  useEffect(() => {
    return () => {
      if (readTimeoutRef.current) {
        window.clearTimeout(readTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    dispatch(setGlobalFilter(e.target.value));
  };

  const clearSearch = () => {
    setSearchValue('');
    dispatch(setGlobalFilter(''));
  };

  const handleCheckboxChange = (checked: boolean) => {
    toggleGlobalSelectAll(checked);
  };

  const handleToggleChange = (enabled: boolean) => {
    setIsToggleActive(enabled);
    onShowBackupToggle?.(enabled);
  };

  return (
    <div className='table-control-container flex items-center gap-2 relative mx-auto my-0 w-[99%] overflow-x-auto mr-5'>
      <div className='flex items-center gap-2 flex-nowrap w-full'>
        {/* Checkbox */}
        <div className='checkbox-section w-[173px] flex-shrink-0'>
          <Checkbox
            isChecked={globalSelectAll}
            Label='Select all setpoints'
            onItemSelect={() => handleCheckboxChange(!globalSelectAll)}
          />
        </div>
        {/* Action Buttons */}
        <div className='action-buttons flex flex-nowrap gap-[8px] items-center flex-shrink-0'>
          <ProtectAction canAccess={rPermission.canReadSetpoints}>
            <button
              className='flex h-[46px] py-[10px] px-[14px] gap-[8px] justify-center items-center bg-[#131f2e] rounded-[8px] border border-solid border-[#4a5463] shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] cursor-pointer hover:bg-[#1a2633] transition-colors'
              disabled={selectedRows.size === 0}
              style={selectedRows.size === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              onClick={handleReadController}
            >
              <ScanIcon />
              <span className="h-[0.9rem] font-['Mulish'] text-[12px] leading-[0.9rem] text-[#f7f9f9] whitespace-nowrap">Read setpoints</span>
            </button>
          </ProtectAction>
          <ProtectAction canAccess={rPermission.canWriteSetpoints}>
            <button
              className='flex h-[46px] py-[10px] px-[14px] gap-[8px] justify-center items-center bg-[#131f2e] rounded-[8px] border border-solid border-[#4a5463] shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] cursor-pointer hover:bg-[#1a2633] transition-colors'
              disabled={!enableWrite}
              style={!enableWrite ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              onClick={handleWriteClick}
            >
              <RepeatIcon />
              <span className="h-[0.9rem] font-['Mulish'] text-[12px] leading-[0.9rem] text-[#f7f9f9] whitespace-nowrap">Write setpoints</span>
            </button>
          </ProtectAction>
          <ProtectAction canAccess={rPermission.canBackupSetpoints}>
            <button
              className='flex h-[46px] py-[10px] px-[14px] gap-[8px] justify-center items-center bg-[#131f2e] rounded-[8px] border border-solid border-[#4a5463] shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] cursor-pointer hover:bg-[#1a2633] transition-colors'
              disabled={!enableWrite}
              style={!enableWrite ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              onClick={handleBackupClick}
            >
              <DatabaseIcon />
              <span className="h-[0.9rem] font-['Mulish'] text-[12px] leading-[0.9rem] text-[#f7f9f9] whitespace-nowrap">Backup setpoints</span>
            </button>
          </ProtectAction>
          <ProtectAction canAccess={rPermission.canRestoreSetpoints}>
            <button
              className='flex h-[46px] py-[10px] px-[14px] gap-[8px] justify-center items-center bg-[#131f2e] rounded-[8px] border border-solid border-[#4a5463] shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] cursor-pointer hover:bg-[#1a2633] transition-colors'
              disabled={selectedRows.size === 0}
              style={selectedRows.size === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              onClick={handleRestoreClick}
            >
              <SendUploadIcon />
              <span className="h-[0.9rem] font-['Mulish'] text-[12px] leading-[1rem] text-[#f7f9f9] whitespace-nowrap">Restore setpoints</span>
            </button>
          </ProtectAction>
          <ProtectAction canAccess={rPermission.canShowBackup}>
            <ToggleOption
              label='Show backup'
              enabled={isToggleActive}
              onChange={handleToggleChange}
              disabled={!rPermission.canShowBackup}
            />
          </ProtectAction>
        </div>
        {/* Search + Filter aligned right */}
        <div className='flex items-center gap-2 ml-auto flex-shrink-0'>
          <div className='flex flex-1 min-w-[250px] max-w-[300px] pt-[10px] pr-[12px] pb-[10px] pl-[12px] gap-[8px] items-center bg-[#001023] rounded-[8px] border border-solid border-[#4a5463] shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]'>
            <SearchIcon />
            <div className='relative flex flex-1 min-w-0 items-center'>
              <input
                type='text'
                className="custom-input flex w-full h-[24px] font-['Mulish'] text-[12px] leading-[24px] text-[#b8c5cc] bg-transparent border-none outline-none pr-[20px]"
                placeholder='Search parameter or description'
                value={searchValue}
                onChange={handleSearchChange}
                style={{ color: searchValue ? '#f7f9f9' : '#b8c5cc' }}
              />
              {searchValue && (
                <button
                  type='button'
                  onClick={() => clearSearch()}
                  className='absolute right-0 text-[#b8c5cc] hover:text-white text-[14px] leading-none cursor-pointer bg-transparent border-none'
                  aria-label='Clear search'
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          <button
            className='filter-btn flex items-center bg-[#131f2e] rounded-[8px] border border-solid border-[#4a5463] shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] cursor-pointer hover:bg-[#1a2633] transition-colors px-[14px] h-[46px]'
            onClick={() => setIsFilterPanelOpen(true)}
          >
            <FilterIcon />
            <span className="h-[20px] font-['Mulish'] text-[12px] font-bold leading-[20px] text-[#f7f9f9] whitespace-nowrap">Filter</span>
            {selectedGroupIds.length > 0 && selectedGroupIds.length < availableGroups.length && (
              <span className='tc-filter-btn-label'>{selectedGroupIds.length}</span>
            )}
          </button>
        </div>
      </div>
      {/* Filter Selector Panel */}
      {isFilterPanelOpen && (
        <SetpointGroupFilterSelector
          groups={availableGroups}
          selectedGroupIds={selectedGroupIds}
          onUpdateSelectedTypes={() => {
            // This callback is used by FilterSelector internally for local state management
          }}
          onClose={() => setIsFilterPanelOpen(false)}
          onApply={(selectedIds: string[]) => {
            onFilterApply(selectedIds);
            setIsFilterPanelOpen(false);
          }}
          title='Filter setpoints'
        />
      )}
    </div>
  );
}
